import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  LinearProgress, Button, Divider, Chip
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  ShoppingCart as OrderIcon,
  CurrencyRupee as RupeeIcon,
  People as PeopleIcon,
  ProductionQuantityLimits as ProductIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  LocalShipping as DispatchIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalEmployees: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    totalDispatches: 0,
    lowStockItems: 0,
    monthlyData: [],
    statusDistribution: [],
    recentActivities: []
  });

  const COLORS = ['#4caf50', '#f44336', '#ff9800', '#2196f3', '#9c27b0', '#00bcd4'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch Products
      const productsRes = await api.get('/inventory/products/');
      let products = [];
      if (Array.isArray(productsRes.data)) {
        products = productsRes.data;
      } else if (productsRes.data?.results) {
        products = productsRes.data.results;
      }

      // Fetch Sales Orders
      const ordersRes = await api.get('/sales/orders/');
      let orders = [];
      if (Array.isArray(ordersRes.data)) {
        orders = ordersRes.data;
      } else if (ordersRes.data?.results) {
        orders = ordersRes.data.results;
      }

      // Fetch Employees
      const empRes = await api.get('/hr/employees/');
      let employees = [];
      if (Array.isArray(empRes.data)) {
        employees = empRes.data;
      } else if (empRes.data?.results) {
        employees = empRes.data.results;
      }

      // Fetch Dispatches
      const dispRes = await api.get('/dispatch/dispatches/');
      let dispatches = [];
      if (Array.isArray(dispRes.data)) {
        dispatches = dispRes.data;
      } else if (dispRes.data?.results) {
        dispatches = dispRes.data.results;
      }

      // Fetch Payments
      const payRes = await api.get('/finance/payments/');
      let payments = [];
      if (Array.isArray(payRes.data)) {
        payments = payRes.data;
      } else if (payRes.data?.results) {
        payments = payRes.data.results;
      }

      // Fetch Expenses
      const expRes = await api.get('/finance/expenses/');
      let expenses = [];
      if (Array.isArray(expRes.data)) {
        expenses = expRes.data;
      } else if (expRes.data?.results) {
        expenses = expRes.data.results;
      }

      // Calculate totals
      let revenue = 0;
      payments.forEach(p => {
        if (p.payment_type === 'received') {
          revenue += parseFloat(p.amount) || 0;
        }
      });

      let totalExpenses = 0;
      expenses.forEach(e => {
        totalExpenses += parseFloat(e.amount) || 0;
      });

      // Count low stock items
      const lowStock = products.filter(p => {
        const current = parseFloat(p.current_stock) || 0;
        const min = parseFloat(p.minimum_stock) || 0;
        return current <= min;
      });

      // Prepare status distribution
      const statusCounts = {};
      orders.forEach(o => {
        statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
      });
      const statusDistribution = Object.keys(statusCounts).map(key => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: statusCounts[key]
      }));

      // Prepare monthly data (last 6 months)
      const monthlyData = [];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonth = new Date().getMonth();
      for (let i = 5; i >= 0; i--) {
        const monthIdx = (currentMonth - i + 12) % 12;
        const monthName = months[monthIdx];
        // Count orders for this month
        const monthOrders = orders.filter(o => {
          const orderDate = new Date(o.order_date);
          return orderDate.getMonth() === monthIdx;
        });
        monthlyData.push({
          month: monthName,
          orders: monthOrders.length,
          revenue: monthOrders.reduce((sum, o) => sum + (parseFloat(o.net_amount) || 0), 0)
        });
      }

      setStats({
        totalProducts: products.length,
        totalOrders: orders.length,
        pendingOrders: orders.filter(o => o.status === 'draft' || o.status === 'confirmed').length,
        totalEmployees: employees.length,
        totalRevenue: revenue,
        totalExpenses: totalExpenses,
        totalDispatches: dispatches.length,
        lowStockItems: lowStock.length,
        monthlyData: monthlyData,
        statusDistribution: statusDistribution,
        recentActivities: orders.slice(0, 5)
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="subtitle2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={0.5}>
                {trend > 0 ? (
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: '#f44336', fontSize: 16 }} />
                )}
                <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                  {Math.abs(trend)}% from last month
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: color + '20',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }} align="center">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Dashboard</Typography>
        <Button variant="outlined" onClick={fetchDashboardData}>
          Refresh Data
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={stats.totalProducts}
            icon={<ProductIcon sx={{ color: '#2E7D32', fontSize: 30 }} />}
            color="#2E7D32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={stats.totalOrders}
            icon={<OrderIcon sx={{ color: '#1976D2', fontSize: 30 }} />}
            color="#1976D2"
            subtitle={`${stats.pendingOrders} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`₹${(stats.totalRevenue || 0).toLocaleString('en-IN')}`}
            icon={<RupeeIcon sx={{ color: '#FF8F00', fontSize: 30 }} />}
            color="#FF8F00"
            trend={12.5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Employees"
            value={stats.totalEmployees}
            icon={<PeopleIcon sx={{ color: '#9C27B0', fontSize: 30 }} />}
            color="#9C27B0"
          />
        </Grid>
        {stats.lowStockItems > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Low Stock Items"
              value={stats.lowStockItems}
              icon={<WarningIcon sx={{ color: '#D32F2F', fontSize: 30 }} />}
              color="#D32F2F"
              subtitle="Needs attention"
            />
          </Grid>
        )}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Orders & Revenue
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" orientation="left" stroke="#1976D2" />
                  <YAxis yAxisId="right" orientation="right" stroke="#FF8F00" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="orders" fill="#1976D2" name="Orders" />
                  <Bar yAxisId="right" dataKey="revenue" fill="#FF8F00" name="Revenue (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Order Status Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.statusDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/inventory')}
              sx={{ py: 2 }}
            >
              Manage Inventory
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => navigate('/sales')}
              sx={{ py: 2 }}
            >
              Sales Orders
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => navigate('/production')}
              sx={{ py: 2 }}
            >
              Log Production
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              variant="contained"
              color="info"
              fullWidth
              onClick={() => navigate('/attendance-reports')}
              sx={{ py: 2 }}
            >
              View Reports
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Dashboard;